import React, { useEffect, useState } from 'react';
import { formatShortDate } from '../../shared/time';
import type { AdditionalFaq } from '../../types/knowledgeQuestions';
import {
  Button,
  Callout,
  CheckProgress,
  Dialog,
  Field,
  IconArchive,
  IconCheckCircle,
  Input,
  Reason,
  Spinner,
  StatusLabel,
  Textarea,
  When,
} from '../../ui/v2';
import {
  canContactSupport,
  CHECK_SOURCES,
  declinedNote,
  isCheckFresh,
  isChecking,
  isStaleCheckError,
  isSupportApproved,
  isWithSupport,
  publishBlockedReason,
  WITH_SUPPORT_REASON,
} from './knowledgeModel';

const MAX_SUPPORT_MESSAGE = 4000;

export const MAX_QUESTION = 500;
export const MAX_ANSWER = 2000;

type Text = { question: string; answer: string };
type Action = 'save' | 'check' | 'publish' | 'archive';
type ActionError = { action: Action; message: string; status: number | null };

export interface FaqEditorProps {
  faq: AdditionalFaq | null;
  /** Prefill when there is no FAQ yet (the caller's question). */
  initialQuestion?: string;
  /** Label for the first save, before an FAQ exists. */
  createLabel?: string;
  canPublish: boolean;
  publishEnabled: boolean;
  onSave: (text: Text) => Promise<unknown>;
  onCheck: (id: number) => Promise<unknown>;
  onPublish: (id: number) => Promise<unknown>;
  onArchive?: (id: number) => Promise<unknown>;
  /** Drafted message for AskArthur support about a flagged answer. */
  onSupportDraft?: (id: number) => Promise<string>;
  onSendToSupport?: (id: number, message: string) => Promise<unknown>;
}

/**
 * One Additional FAQ. Saving new text starts the check against the store's
 * menu and info (the backend runs it). An Owner or Manager gets one Publish
 * button instead of Save: it saves any edit, shows the check in a dialog
 * (skipping one that passed in the last 20 minutes) and asks them to
 * confirm. Nothing goes live until they do, so an edit to a published
 * answer leaves Arthur on the published text.
 */
export const FaqEditor: React.FC<FaqEditorProps> = ({
  faq,
  initialQuestion = '',
  createLabel = 'Save',
  canPublish,
  publishEnabled,
  onSave,
  onCheck,
  onPublish,
  onArchive,
  onSupportDraft,
  onSendToSupport,
}) => {
  const serverQuestion = faq?.question ?? initialQuestion;
  const serverAnswer = faq?.answer ?? '';
  const [question, setQuestion] = useState(serverQuestion);
  const [answer, setAnswer] = useState(serverAnswer);
  const [busy, setBusy] = useState<Action | null>(null);
  const [error, setError] = useState<ActionError | null>(null);
  const [touched, setTouched] = useState(false);
  const [supportOpen, setSupportOpen] = useState(false);
  const [publishOpen, setPublishOpen] = useState(false);

  useEffect(() => {
    setQuestion(serverQuestion);
    setAnswer(serverAnswer);
    setTouched(false);
  }, [faq?.id, serverQuestion, serverAnswer]);

  // Nothing left to confirm: published (or an edit put back to the live
  // text), or removed or handed to support from another tab.
  useEffect(() => {
    if (
      publishOpen &&
      busy == null &&
      (faq?.status === 'published' ||
        faq?.status === 'archived' ||
        faq?.status === 'with_support')
    ) {
      setPublishOpen(false);
    }
  }, [publishOpen, busy, faq?.status]);

  const dirty =
    question.trim() !== serverQuestion.trim() ||
    answer.trim() !== serverAnswer.trim() ||
    !faq;
  const checking = isChecking(faq);
  const withSupport = isWithSupport(faq);
  const publishMode = canPublish && publishEnabled;
  const questionError =
    touched && !question.trim() ? 'Write the question.' : undefined;
  const answerError =
    touched && !answer.trim()
      ? 'Write the answer Arthur should give.'
      : undefined;

  const run = async (name: Action, action: () => Promise<unknown>) => {
    setBusy(name);
    setError(null);
    try {
      await action();
    } catch (e) {
      setError({
        action: name,
        message: (e as Error).message,
        status: (e as { status?: number | null }).status ?? null,
      });
    } finally {
      setBusy(null);
    }
  };

  const written = () => {
    setTouched(true);
    return !!question.trim() && !!answer.trim();
  };
  const saveText = () =>
    run('save', () =>
      onSave({ question: question.trim(), answer: answer.trim() }),
    );

  const save = () => {
    if (written()) void saveText();
  };

  /** Save an edit (which starts the check), check a stale result, or go straight to confirming. */
  const publish = () => {
    if (!written()) return;
    setError(null);
    setPublishOpen(true);
    if (dirty) {
      void saveText();
    } else if (
      faq &&
      (faq.status === 'draft' || (faq.status === 'ready' && !isCheckFresh(faq)))
    ) {
      void run('check', () => onCheck(faq.id));
    }
  };

  const closePublish = () => {
    setPublishOpen(false);
    setError(null);
  };

  const blocked =
    publishMode && faq ? publishBlockedReason(faq, { dirty }) : null;
  /** Saving checks; this is for a check that failed or never ran. */
  const retryCheck = !!faq && !dirty && faq.status === 'draft';
  const showResult =
    !!faq && !dirty && !checking && !withSupport && faq.check_verdict !== '';
  const supportAvailable =
    !!faq &&
    !dirty &&
    !!onSupportDraft &&
    !!onSendToSupport &&
    canContactSupport(faq);
  const declined = faq ? declinedNote(faq) : null;

  return (
    <div className="flex flex-col gap-4">
      {withSupport && faq && (
        <Callout tone="accent" title="With AskArthur support">
          <p className="mt-1 text-v2-12 text-v2-fg-3">
            Sent <When value={faq.support_request?.created_at} />.
          </p>
          <p className="mt-1 text-v2-13 text-v2-fg-2">{WITH_SUPPORT_REASON}</p>
        </Callout>
      )}

      {declined != null && (
        <Callout tone="warning" title="AskArthur support couldn’t approve this">
          {declined && (
            <p className="mt-1 whitespace-pre-line text-v2-13 text-v2-fg-2">
              {declined}
            </p>
          )}
        </Callout>
      )}

      {faq?.is_live && faq.has_unpublished_changes && (
        <Callout tone="neutral" title="Arthur still gives the published answer">
          <p className="mt-1 text-v2-13 text-v2-fg-2">{faq.published_answer}</p>
          <p className="mt-1 text-v2-12 text-v2-fg-3">
            Publish your edit to replace it.
          </p>
        </Callout>
      )}

      <Field
        label="Question"
        error={questionError}
        hint="Write it the way a caller would ask."
      >
        {(id, describedBy) => (
          <Input
            id={id}
            aria-describedby={describedBy}
            value={question}
            maxLength={MAX_QUESTION}
            disabled={withSupport}
            invalid={!!questionError}
            onChange={(e) => setQuestion(e.target.value)}
          />
        )}
      </Field>
      <Field label="Answer" error={answerError}>
        {(id, describedBy) => (
          <Textarea
            id={id}
            aria-describedby={describedBy}
            rows={4}
            value={answer}
            maxLength={MAX_ANSWER}
            disabled={withSupport}
            invalid={!!answerError}
            onChange={(e) => setAnswer(e.target.value)}
          />
        )}
      </Field>

      {checking && !publishOpen && (
        <div
          role="status"
          aria-live="polite"
          className="flex items-center gap-2 text-v2-13 text-v2-fg-2"
        >
          <Spinner className="text-v2-accent" />
          Fact-checking for conflicts…
        </div>
      )}

      {/* Publish runs the check again, so only Save needs a separate retry. */}
      {!publishMode && !checking && faq?.check_error && !dirty && (
        <Callout tone="danger" title="The conflict check didn’t finish">
          Try again in a moment.
        </Callout>
      )}

      {showResult && faq && (
        <CheckResult
          faq={faq}
          onContactSupport={
            supportAvailable ? () => setSupportOpen(true) : undefined
          }
        />
      )}

      {faq && onSupportDraft && onSendToSupport && (
        <SupportDialog
          open={supportOpen}
          faqId={faq.id}
          onClose={() => setSupportOpen(false)}
          loadDraft={onSupportDraft}
          onSend={onSendToSupport}
        />
      )}

      {publishMode && (
        <PublishDialog
          open={publishOpen}
          faq={faq}
          busy={busy}
          error={error}
          onClose={closePublish}
          onPublish={(id) => void run('publish', () => onPublish(id))}
          onCheck={(id) => void run('check', () => onCheck(id))}
          onRetry={publish}
          onContactSupport={
            supportAvailable
              ? () => {
                  closePublish();
                  setSupportOpen(true);
                }
              : undefined
          }
        />
      )}

      {error && !publishOpen && (
        <Callout tone="danger" title="That didn’t go through">
          {error.message}
        </Callout>
      )}

      <div className="flex flex-wrap items-center gap-2">
        {faq && <PublishedState faq={faq} />}
        <div className="ml-auto flex flex-wrap items-center gap-2">
          {onArchive &&
            faq &&
            canPublish &&
            faq.status !== 'archived' &&
            !withSupport && (
              <Button
                size="sm"
                variant="ghost"
                leading={<IconArchive size={14} />}
                loading={busy === 'archive'}
                disabled={busy != null}
                onClick={() => void run('archive', () => onArchive(faq.id))}
              >
                Remove
              </Button>
            )}
          {publishMode ? (
            <Button
              size="sm"
              variant="primary"
              disabledReason={blocked ?? undefined}
              disabled={busy != null}
              onClick={publish}
            >
              Publish
            </Button>
          ) : dirty ? (
            <Button
              size="sm"
              variant="primary"
              loading={busy === 'save'}
              disabled={busy != null}
              onClick={save}
            >
              {faq ? 'Save' : createLabel}
            </Button>
          ) : (
            retryCheck && (
              <Button
                size="sm"
                variant="primary"
                loading={busy === 'check'}
                disabled={busy != null}
                onClick={() => void run('check', () => onCheck(faq.id))}
              >
                {faq.check_error ? 'Try again' : 'Check for conflicts'}
              </Button>
            )
          )}
        </div>
      </div>
      {!publishMode && faq?.status === 'ready' && (
        <Reason>
          {canPublish
            ? 'Publishing to Arthur isn’t turned on for this store yet.'
            : 'Ready for an owner or manager to publish.'}
        </Reason>
      )}
    </div>
  );
};

/** Whether Arthur gives this answer: published (and when), or not. */
const PublishedState: React.FC<{ faq: AdditionalFaq }> = ({ faq }) =>
  faq.is_live ? (
    <StatusLabel size="sm" tone="success">
      {faq.published_at
        ? `Published ${formatShortDate(faq.published_at)}`
        : 'Published'}
    </StatusLabel>
  ) : (
    <StatusLabel size="sm" tone="neutral" hollow>
      Not published
    </StatusLabel>
  );

type PublishStep = 'checking' | 'ready' | 'flagged' | 'failed';

function publishStep(
  faq: AdditionalFaq | null,
  busy: Action | null,
  error: ActionError | null,
): PublishStep {
  if (error && error.action !== 'publish') return 'failed';
  if (busy === 'save' || busy === 'check' || !faq) return 'checking';
  switch (faq.status) {
    case 'ready':
      return 'ready';
    case 'conflict':
    case 'uncertain':
      return 'flagged';
    case 'draft':
      return faq.check_error ? 'failed' : 'checking';
    default:
      return 'checking';
  }
}

/**
 * Publish, step by step: the conflict check (polled by the page), then a
 * confirm. Closing it before confirming leaves the answer unpublished.
 */
const PublishDialog: React.FC<{
  open: boolean;
  faq: AdditionalFaq | null;
  busy: Action | null;
  error: ActionError | null;
  onClose: () => void;
  onPublish: (id: number) => void;
  onCheck: (id: number) => void;
  onRetry: () => void;
  onContactSupport?: () => void;
}> = ({
  open,
  faq,
  busy,
  error,
  onClose,
  onPublish,
  onCheck,
  onRetry,
  onContactSupport,
}) => {
  const step = publishStep(faq, busy, error);
  const publishing = busy === 'publish';
  const publishError = error?.action === 'publish' ? error : null;

  let title: string;
  let body: React.ReactNode;
  let footer: React.ReactNode = null;
  if (step === 'checking' || !faq) {
    title = 'Checking for conflicts';
    body = (
      <CheckProgress
        message={
          busy === 'save'
            ? 'Saving your answer…'
            : 'Comparing your answer with everything Arthur already knows…'
        }
        steps={CHECK_SOURCES}
        note="You can close this. The check keeps running."
      />
    );
  } else if (step === 'ready') {
    const approved = isSupportApproved(faq);
    // Store info changed since the check: ask for a fresh one instead of
    // claiming the answer is clear. Other failures retry Publish.
    const stale = publishError != null && isStaleCheckError(publishError);
    title = stale ? 'Check again before publishing' : 'No conflicts found';
    body = (
      <div className="flex flex-col gap-3">
        {stale ? (
          <Callout tone="warning">
            {publishError.message}
            {approved && (
              <p className="mt-1 text-v2-12 text-v2-fg-3">
                AskArthur support approved this answer. Checking again replaces
                that approval with a new automatic check.
              </p>
            )}
          </Callout>
        ) : (
          <p className="v2-rise-in flex items-center gap-2 text-v2-13 text-v2-success">
            <IconCheckCircle size={16} />
            {approved
              ? 'AskArthur support approved this answer.'
              : 'Good to go - no conflicts!'}
          </p>
        )}
        <dl className="rounded-v2 border border-v2-line bg-v2-sunken/60 px-3 py-2.5 text-v2-13">
          <dt className="text-v2-12 text-v2-fg-3">Question</dt>
          <dd className="text-v2-fg">{faq.question}</dd>
          <dt className="mt-2 text-v2-12 text-v2-fg-3">Answer</dt>
          <dd className="whitespace-pre-line text-v2-fg">{faq.answer}</dd>
        </dl>
        {publishError && !stale && (
          <Callout tone="danger" title="That didn’t go through">
            {publishError.message}
          </Callout>
        )}
        {!stale && (
          <p className="text-v2-12 text-v2-fg-3">
            Arthur starts giving this answer on calls once you publish.
          </p>
        )}
      </div>
    );
    footer = (
      <>
        <Button variant="ghost" onClick={onClose} disabled={publishing}>
          Not now
        </Button>
        {stale ? (
          <Button variant="primary" onClick={() => onCheck(faq.id)}>
            Check again
          </Button>
        ) : (
          <Button
            variant="primary"
            loading={publishing}
            onClick={() => onPublish(faq.id)}
          >
            Publish to Arthur
          </Button>
        )}
      </>
    );
  } else if (step === 'flagged') {
    title = findingTitle(faq);
    body = (
      <div className="flex flex-col gap-3">
        <CheckFindings faq={faq} />
        <p className="text-v2-13 text-v2-fg-2">
          Nothing was published. Edit the answer, then publish again.
        </p>
      </div>
    );
    footer = (
      <>
        {onContactSupport && (
          <Button variant="secondary" onClick={onContactSupport}>
            Contact support
          </Button>
        )}
        <Button variant="primary" onClick={onClose}>
          Edit answer
        </Button>
      </>
    );
  } else {
    title =
      error?.action === 'save'
        ? 'That didn’t go through'
        : 'The check didn’t finish';
    body = (
      <p className="text-v2-13 text-v2-fg-2">
        {error?.message ?? 'Try again in a moment.'} Nothing was published.
      </p>
    );
    footer = (
      <>
        <Button variant="ghost" onClick={onClose}>
          Close
        </Button>
        <Button variant="primary" onClick={onRetry}>
          Try again
        </Button>
      </>
    );
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      dismissible={!publishing}
      title={title}
      footer={footer}
    >
      {body}
    </Dialog>
  );
};

const findingTitle = (faq: AdditionalFaq) =>
  faq.check_verdict === 'conflict'
    ? 'Arthur already says something different'
    : 'The check couldn’t confirm this answer';

/** An FAQ source is quoted as "Q: …\nA: …"; split it so the answer stands out. */
function splitFaqQuote(
  quote: string,
): { question: string; answer: string } | null {
  const match = /^\s*Q:\s*([\s\S]*?)\s*A:\s*([\s\S]+?)\s*$/.exec(quote);
  return match ? { question: match[1], answer: match[2] } : null;
}

/** What the check found, then each source it quotes on its own card. */
const CheckFindings: React.FC<{ faq: AdditionalFaq }> = ({ faq }) => (
  <>
    {faq.check_summary && (
      <p className="mt-1 text-v2-13 text-v2-fg-2">{faq.check_summary}</p>
    )}
    {faq.check_citations.length > 0 && (
      <ul
        aria-label="Sources"
        className="mt-3 divide-y divide-v2-line overflow-hidden rounded-v2 border border-v2-line bg-v2-surface"
      >
        {faq.check_citations.map((c, i) => {
          const qa = splitFaqQuote(c.quote);
          return (
            <li key={`${c.source_id}:${i}`} className="px-3 py-2.5">
              <p className="text-v2-12 font-medium text-v2-fg-3">
                {c.source_label}
              </p>
              {qa ? (
                <>
                  <p className="mt-1 text-v2-12 text-v2-fg-3">{qa.question}</p>
                  <p className="mt-0.5 whitespace-pre-line text-v2-13 font-medium text-v2-fg">
                    {qa.answer}
                  </p>
                </>
              ) : (
                <p className="mt-1 whitespace-pre-line text-v2-13 text-v2-fg">
                  {c.quote}
                </p>
              )}
            </li>
          );
        })}
      </ul>
    )}
  </>
);

const CheckResult: React.FC<{
  faq: AdditionalFaq;
  onContactSupport?: () => void;
}> = ({ faq, onContactSupport }) => {
  if (faq.check_verdict === 'no_conflict') {
    const approved = isSupportApproved(faq);
    return (
      <div role="status" className="flex flex-col gap-1">
        <p className="flex items-center gap-2 text-v2-13 text-v2-success">
          <IconCheckCircle size={15} />
          {approved
            ? 'AskArthur support approved this answer.'
            : 'No conflicts with your menu, hours or store info.'}
        </p>
        {approved && faq.support_request?.resolution_note && (
          <p className="whitespace-pre-line text-v2-12 text-v2-fg-3">
            {faq.support_request.resolution_note}
          </p>
        )}
      </div>
    );
  }
  const conflict = faq.check_verdict === 'conflict';
  return (
    <Callout tone={conflict ? 'danger' : 'warning'} title={findingTitle(faq)}>
      <CheckFindings faq={faq} />
      {onContactSupport && (
        <p className="mt-3 text-v2-12 text-v2-fg-3">
          Think the check got it wrong?{' '}
          <button
            type="button"
            onClick={onContactSupport}
            className="font-medium text-v2-accent hover:text-v2-accent-hover"
          >
            Contact support
          </button>
        </p>
      )}
    </Callout>
  );
};

/** Drafted message to AskArthur support; the store can edit it before sending. */
const SupportDialog: React.FC<{
  open: boolean;
  faqId: number;
  onClose: () => void;
  loadDraft: (id: number) => Promise<string>;
  onSend: (id: number, message: string) => Promise<unknown>;
}> = ({ open, faqId, onClose, loadDraft, onSend }) => {
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setMessage('');
    loadDraft(faqId)
      .then((draft) => {
        if (!cancelled) setMessage(draft);
      })
      .catch((e: Error) => {
        if (!cancelled) setError(e.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, faqId, loadDraft]);

  const send = async () => {
    setSending(true);
    setError(null);
    try {
      await onSend(faqId, message.trim());
      onClose();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      dismissible={!sending}
      title="Contact support"
      description="We drafted this from the check. Edit anything before you send it."
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={sending}>
            Cancel
          </Button>
          <Button
            variant="primary"
            loading={sending}
            disabledReason={
              loading
                ? 'Drafting your message…'
                : !message.trim()
                  ? 'Write a message first.'
                  : undefined
            }
            onClick={() => void send()}
          >
            Send to support
          </Button>
        </>
      }
    >
      {loading ? (
        <div
          role="status"
          className="flex items-center gap-2 py-6 text-v2-13 text-v2-fg-3"
        >
          <Spinner />
          Drafting your message…
        </div>
      ) : (
        <Field label="Message">
          {(id, describedBy) => (
            <Textarea
              id={id}
              aria-describedby={describedBy}
              rows={10}
              value={message}
              maxLength={MAX_SUPPORT_MESSAGE}
              onChange={(e) => setMessage(e.target.value)}
              data-autofocus="true"
            />
          )}
        </Field>
      )}
      {error && (
        <Callout tone="danger" title="That didn’t go through" className="mt-3">
          {error}
        </Callout>
      )}
    </Dialog>
  );
};
